import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, KeyRound } from 'lucide-react';
import { UserRole } from '@tutor/shared';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
} from '@tutor/ui';
import { ApiError, api } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-client';
import { useProfile } from '../../hooks/queries';
import { useAuthStore } from '../../stores/auth.store';
import { formatDate, formatDateTime } from '../../lib/format';

export function ProfilePage() {
  const { data, isLoading, isError } = useProfile();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [initialised, setInitialised] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (isLoading) return <LoadingState />;
  if (isError || !data) return <ErrorState description="We could not load your profile." />;

  // Seed the form from the server exactly once, so typing is not overwritten by refetches.
  if (!initialised) {
    setFirstName(data.user.firstName);
    setLastName(data.user.lastName);
    setInitialised(true);
  }

  const onSave = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setSaving(true);
    setNotice(null);
    setErrorMessage(null);

    try {
      await api.patch('/api/me/profile', { firstName, lastName });
      await queryClient.invalidateQueries({ queryKey: queryKeys.profile.me });
      setNotice('Your profile has been updated.');
    } catch (error) {
      setErrorMessage(error instanceof ApiError ? error.message : 'We could not save your changes.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageHeader title="Profile" description="Your account details." />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader title="Personal details" />
            <CardBody>
              <form onSubmit={onSave} className="space-y-4">
                {notice && <Alert tone="success">{notice}</Alert>}
                {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}

                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="First name"
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                  />
                  <Input
                    label="Last name"
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                  />
                </div>

                <Input label="Email address" value={data.user.email} disabled hint="Contact support to change this." />

                <Button type="submit" isLoading={saving}>
                  Save changes
                </Button>
              </form>
            </CardBody>
          </Card>

          {data.tutorProfile && (
            <Card>
              <CardHeader
                title="Tutor profile"
                action={
                  data.tutorProfile.isVerified ? (
                    <Badge tone="success">
                      <BadgeCheck className="h-3 w-3" aria-hidden />
                      Verified
                    </Badge>
                  ) : undefined
                }
              />
              <CardBody className="space-y-4">
                {data.tutorProfile.headline && (
                  <p className="text-sm font-medium text-slate-900">{data.tutorProfile.headline}</p>
                )}
                {data.tutorProfile.bio && <p className="text-sm text-slate-700">{data.tutorProfile.bio}</p>}

                <div>
                  <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">Subjects</p>
                  <div className="flex flex-wrap gap-1.5">
                    {data.tutorProfile.subjects.map((subject) => (
                      <Badge key={subject.id} tone="brand">
                        {subject.name}
                      </Badge>
                    ))}
                  </div>
                </div>

                {data.tutorProfile.qualifications.length > 0 && (
                  <div>
                    <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-slate-500">
                      Qualifications
                    </p>
                    <ul className="space-y-1">
                      {data.tutorProfile.qualifications.map((qualification) => (
                        <li key={qualification} className="text-sm text-slate-700">
                          {qualification}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <dl className="grid grid-cols-2 gap-3 border-t border-slate-100 pt-3 text-sm">
                  <div>
                    <dt className="text-slate-500">Experience</dt>
                    <dd className="font-medium text-slate-900">
                      {data.tutorProfile.yearsExperience ?? '—'} years
                    </dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Hourly rate</dt>
                    <dd className="font-medium text-slate-900">
                      {data.tutorProfile.hourlyRate !== null
                        ? `${data.tutorProfile.currency} ${data.tutorProfile.hourlyRate.toFixed(2)}`
                        : '—'}
                    </dd>
                  </div>
                </dl>
              </CardBody>
            </Card>
          )}

          {data.studentProfile && (
            <Card>
              <CardHeader title="Student profile" />
              <CardBody>
                <dl className="grid gap-4 sm:grid-cols-2">
                  <Field label="Education level" value={data.studentProfile.educationLevel?.replace('_', ' ') ?? '—'} />
                  <Field label="Year group" value={data.studentProfile.yearGroup ?? '—'} />
                  <Field label="School" value={data.studentProfile.school ?? '—'} />
                  <Field label="Target grade" value={data.studentProfile.targetGrade ?? '—'} />
                  <Field label="Guardian" value={data.studentProfile.guardianName ?? '—'} />
                  <Field label="Guardian email" value={data.studentProfile.guardianEmail ?? '—'} />
                </dl>
              </CardBody>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardBody className="text-center">
              <Avatar name={data.user.fullName} src={data.user.avatarUrl} size="xl" className="mx-auto" />
              <p className="mt-3 font-medium text-slate-900">{data.user.fullName}</p>
              <p className="text-sm text-slate-500">{data.user.email}</p>
              <Badge tone="brand" className="mt-2">
                {user?.role === UserRole.TUTOR ? 'Tutor' : 'Student'}
              </Badge>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Account" />
            <CardBody className="space-y-2 text-sm">
              <Row label="Member since" value={formatDate(data.user.createdAt)} />
              <Row
                label="Last sign in"
                value={data.user.lastLoginAt ? formatDateTime(data.user.lastLoginAt) : '—'}
              />
              <Row label="Time zone" value={data.user.timezone} />
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Security" />
            <CardBody>
              <p className="mb-3 text-sm text-slate-600">
                Changing your password signs you out of every device.
              </p>
              <Button
                variant="outline"
                fullWidth
                leftIcon={<KeyRound className="h-4 w-4" aria-hidden />}
                onClick={() => window.location.assign('/forgot-password')}
              >
                Change password
              </Button>
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-900">{value}</dd>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900">{value}</span>
    </div>
  );
}
